/**
 * printPatientCard — F10, whole-card print (D8③).
 *
 * Client-side print of what the patient card already holds in memory.
 * Reuses the repo's established hidden-iframe `window.print()` pattern
 * (`printToken.js` / `printSampleLabel.js`) rather than the in-app blob-PDF
 * viewer (`FileViewerModal`, which exists to display a PDF the server
 * already generated) — there is no server PDF here, so that viewer doesn't
 * fit. Never `window.open`.
 *
 * THE HARD CONSTRAINT (bk-790): this module must NEVER be able to reach an
 * IPD document endpoint (invoice / diagnostics-bill / discharge-summary /
 * dues-ack) — fetching one of those mints a per-organisation sequential
 * legal document serial, and a "print everything" action that swept them
 * would mint a number for every document in a patient's whole admission
 * history in one click. Every value this module renders is a field ALREADY
 * present on the in-memory `sections` map (or its own pure derivations) —
 * this file imports NO `services/api/*` module, and the accompanying test
 * (`__tests__/printPatientCard.test.jsx`) statically greps this file's own
 * source for every known document-fetching import specifier / endpoint
 * fragment as a standing proof, not just a mock that happened not to fire.
 *
 * The three IPD documents stay separate, click-only PDFs (`AdmissionDocumentButtons`,
 * `getAdmissionDocumentContent`) — this module prints their STATE as plain
 * text (label + INTERIM/DRAFT/FINAL/CANCELLED/NOT_ISSUED band, exactly what
 * the on-screen button would show), reusing the SAME pure label derivation
 * (`utils/admissionDocumentLabels.js`) so the two surfaces can't disagree —
 * never a clickable control, never a fetch.
 */
import { escapeHtml } from './escapeHtml';
import { formatDateIST, formatDayIST } from './formatIST';
import { resolveUhid } from './uhid';
import { DOC_BUTTON_META, bandedLabel, reasonFor } from './admissionDocumentLabels';
import { TIMELINE_KINDS, TIMELINE_TYPE_FILTER } from '../constants/patientCard';
import { readLane } from './patientCardLanes';
import { ADMISSION_STATUS_LABELS } from '../constants/admission';

// Same sentence PatientTimeline.jsx renders in its on-screen
// `procedures-truncated-notice` (bk-819, F7-F, owner-ratified copy) — kept
// as ONE exported string so print can never drift from the live wording.
export const PROCEDURES_TRUNCATED_NOTICE_TITLE = 'Not all procedures are shown';
export const PROCEDURES_TRUNCATED_NOTICE_BODY =
  'This patient has a very large number of treatment plans, so this page shows the most recent ones. '
  + 'All of their plans are listed on the Patient plans screen.';

const FILTER_LABEL = {
  [TIMELINE_TYPE_FILTER.ALL]: 'All records',
  [TIMELINE_TYPE_FILTER.OPD]: 'OPD only',
  [TIMELINE_TYPE_FILTER.IPD]: 'IPD only',
};

/** Same collapsed-row summary `TimelineRow.jsx` derives on screen — kept as
 * a small local pure function (not shared) since it is plain date/label
 * formatting, not owner-authored copy. */
function rowSummary(row) {
  const { ref, bucket, item } = row;
  const isAdmission = ref.kind === TIMELINE_KINDS.ADMISSION;
  const dateLabel = isAdmission
    ? `${formatDayIST(ref.day_ist)} → ${item?.ends_at ? formatDateIST(item.ends_at) : 'Ongoing'}`
    : formatDayIST(ref.day_ist);
  const subLine = isAdmission
    ? [item?.ward_name, item?.bed_label].filter(Boolean).join(' · ') || '—'
    : (item?.doctor_name ? `Dr. ${item.doctor_name}` : '—');
  const statusLabel = isAdmission
    ? (ADMISSION_STATUS_LABELS[item?.status] || item?.status || '—')
    : (item?.status || '—');
  return { bucket, dateLabel, subLine, statusLabel };
}

/** Plain-text document state lines for one admission — reads ONLY the
 * already-resolved `DocumentDescriptor` fields, never a click handler, never
 * an import of `admissionDischargeApi`. Mirrors `AdmissionDocumentButtons`'
 * label text exactly (shared derivation) so print never disagrees with the
 * live buttons. */
function admissionDocumentLines(documentsSection, admissionId) {
  // ONE shared lane guard (Q2) — an unreadable documents lane yields no
  // descriptors, so the printed row omits the document lines rather than
  // printing them as "not issued".
  const { items: documentEntries } = readLane(documentsSection);
  const entry = documentEntries.find((d) => d?.admission_id === admissionId);
  const docList = entry?.documents || [];
  return DOC_BUTTON_META.map((meta) => {
    const descriptor = docList.find((d) => d.doc_type === meta.docType);
    if (!descriptor) return null;
    const label = bandedLabel(meta, descriptor.state);
    const detail = descriptor.available
      ? (descriptor.document_number ? `No. ${descriptor.document_number}` : 'Available')
      : reasonFor(meta.docType, descriptor.unavailable_reason_code);
    return `${label}: ${detail}`;
  }).filter(Boolean);
}

function rowHtml(row, documentsSection) {
  const { ref, item } = row;
  const isAdmission = ref.kind === TIMELINE_KINDS.ADMISSION;
  const { bucket, dateLabel, subLine, statusLabel } = rowSummary(row);
  const docLines = isAdmission ? admissionDocumentLines(documentsSection, item?.admission_id) : [];
  return `
    <div class="row">
      <div class="row-top">
        <span class="badge badge-${bucket}">${escapeHtml(bucket)}</span>
        <span class="row-date">${escapeHtml(dateLabel)}</span>
        <span class="row-status">${escapeHtml(statusLabel)}</span>
      </div>
      <div class="row-sub">${escapeHtml(subLine)}</div>
      ${docLines.length > 0 ? `
        <div class="row-docs">
          ${docLines.map((line) => `<div class="row-doc-line">${escapeHtml(line)}</div>`).join('')}
        </div>` : ''}
    </div>`;
}

function yearGroupHtml(year, rows, documentsSection) {
  return `
    <div class="year-group">
      <div class="year-heading">${escapeHtml(year)} <span class="year-count">· ${rows.length} record${rows.length === 1 ? '' : 's'}</span></div>
      ${rows.map((row) => rowHtml(row, documentsSection)).join('')}
    </div>`;
}

/**
 * printPatientCard — builds the printable HTML document from data ALREADY
 * loaded on the card (zero requests) and prints it via a hidden iframe.
 *
 *   patient             — `sections.patient.item` (PatientResponse), may be null
 *   generatedAt         — the aggregator envelope's `generated_at` (ISO+offset)
 *   typeFilter          — the ACTIVE `TIMELINE_TYPE_FILTER` value (All/OPD/IPD
 *                          the admin currently has selected on screen)
 *   yearGroups          — `[[year, rows[]], ...]`, the SAME array PatientTimeline
 *                          renders from (already sorted, already filtered by
 *                          `typeFilter`, and NEVER capped — the 20/50 reveal
 *                          limit is an on-screen-only affordance; this function
 *                          prints every row in every year group)
 *   proceduresTruncated — bool, same boolean driving the on-screen notice
 *   documentsSection    — `sections.documents` (SectionObject), for the IPD
 *                          document state lines
 */
export function printPatientCard({
  patient, generatedAt, typeFilter, yearGroups, proceduresTruncated, documentsSection,
}) {
  const name = patient
    ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || 'Unnamed patient'
    : 'Unnamed patient';
  const uhid = resolveUhid({ uhid: patient?.uhid, patient_id: patient?.patient_id });
  const registered = formatDateIST(patient?.created_at) || '—';
  const generatedLabel = formatDateIST(generatedAt) || '—';
  const filterLabel = FILTER_LABEL[typeFilter] || FILTER_LABEL[TIMELINE_TYPE_FILTER.ALL];

  const groups = Array.isArray(yearGroups) ? yearGroups : [];
  const totalRows = groups.reduce((sum, [, rows]) => sum + rows.length, 0);

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  @page { size: A4; margin: 15mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif; font-size: 12px; color: #000; background: #fff; }
  .header { border-bottom: 2px solid #000; padding-bottom: 3mm; margin-bottom: 4mm; }
  .patient-name { font-size: 20px; font-weight: 800; }
  .header-row { display: flex; gap: 6mm; margin-top: 1.5mm; font-size: 12px; }
  .header-row span { font-weight: 600; }
  .meta { margin-top: 1.5mm; font-size: 10.5px; color: #333; }
  .filter-note { margin-bottom: 3mm; font-size: 11px; font-weight: 700; }
  .notice { border: 1px solid #000; padding: 2.5mm; margin-bottom: 4mm; }
  .notice-title { font-weight: 800; font-size: 12px; }
  .notice-body { font-size: 10.5px; margin-top: 1mm; }
  .year-group { margin-bottom: 4mm; page-break-inside: avoid; }
  .year-heading { font-weight: 800; font-size: 13px; border-bottom: 1px solid #000; padding-bottom: 1mm; margin-bottom: 2mm; }
  .year-count { font-weight: 500; }
  .row { border-bottom: 1px dashed #999; padding: 1.5mm 0; page-break-inside: avoid; }
  .row-top { display: flex; align-items: center; gap: 2mm; }
  .badge { font-size: 9.5px; font-weight: 800; border: 1px solid #000; padding: 0.3mm 1.5mm; border-radius: 3px; }
  .row-date { font-weight: 700; font-size: 11.5px; }
  .row-status { font-size: 10.5px; border: 1px solid #666; border-radius: 8px; padding: 0.2mm 1.5mm; }
  .row-sub { font-size: 10.5px; color: #333; margin-top: 0.5mm; }
  .row-docs { margin-top: 1mm; padding-left: 3mm; }
  .row-doc-line { font-size: 10px; color: #000; }
  .empty { font-size: 11px; font-style: italic; color: #333; }
  .footer { margin-top: 6mm; font-size: 9.5px; color: #666; border-top: 1px solid #999; padding-top: 1.5mm; }
</style>
</head>
<body>
  <div class="header">
    <div class="patient-name">${escapeHtml(name)}</div>
    <div class="header-row">
      <span>UHID: ${escapeHtml(uhid || '—')}</span>
      <span>Registered: ${escapeHtml(registered)}</span>
    </div>
    <div class="meta">Generated ${escapeHtml(generatedLabel)}</div>
  </div>

  <div class="filter-note">Showing: ${escapeHtml(filterLabel)}</div>

  ${proceduresTruncated ? `
  <div class="notice">
    <div class="notice-title">${escapeHtml(PROCEDURES_TRUNCATED_NOTICE_TITLE)}</div>
    <div class="notice-body">${escapeHtml(PROCEDURES_TRUNCATED_NOTICE_BODY)}</div>
  </div>` : ''}

  ${totalRows === 0
    ? '<div class="empty">No visits or admissions recorded.</div>'
    : groups.map(([year, rows]) => yearGroupHtml(year, rows, documentsSection)).join('')}

  <div class="footer">Printed from ArogyaKhata — this is a summary of the patient's record, not a legal or billing document.</div>
</body>
</html>`;

  printHtml(html);
}

// Shared hidden-iframe print mechanism — identical plumbing to
// `printToken.js` / `printSampleLabel.js`, kept as its own local copy per
// this repo's established per-print-surface convention (each print util
// owns its own copy rather than a shared abstraction this task doesn't
// concretely need).
function printHtml(html) {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
  document.body.appendChild(iframe);

  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();

  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
    }, 1000);
  };

  iframe.onload = () => doPrint();
  setTimeout(() => doPrint(), 500);
}
